import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import moment from "moment"; // ✅ Correct default import
import { useNavigate } from "react-router-dom";
import { confirm } from "../../common/Confirm/confirm";
import { toast } from '../../common/Toast/toast';
import { useAuth } from "../../../../context/AuthContext";
import NewCustomDataTable from "../../common/NewCustomDataTable.jsx";
import CourseModule from "./CourseModule.jsx";
import PopupDone, { usePopupDone } from "../../common/PopupDone/PopupDone.jsx";
import "../../Trainer/TrainerDashboard.css";
import "../../common/lms-pages.css";
import {
  getcourses,
  deletecourses,
} from "../../../../services/NewCourseModule.js";
import debounce from 'lodash/debounce'; // Add import at top
import FilterSheet from '../../common/FilterSheet/FilterSheet';

const ListCourseModule = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("name");
  const [details, setDetails] = useState(null);
  const [formModal, setFormModal] = useState(null);
  // A change saved in either popup is confirmed in it (the done panel),
  // which then closes itself. The form, opened over the view, is newer.
  const { done: detailsDone } = usePopupDone(Boolean(details));
  const { done: formDone } = usePopupDone(Boolean(formModal));
  const [loadingMore, setLoadingMore] = useState(false);

  // The box holds what was typed; the two below hold what the list is
  // actually filtered by, so a keystroke does not refetch on its own.
  const [uiSearchTerm, setUiSearchTerm] = useState("");
  const [uiSearchField, setUiSearchField] = useState("name");

  // Read before the fetch awaits anything: a reset mid-flight would turn
  // an append into a replace and snap the list back to its first page.
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listEndRef = useRef(null);
  const navigate = useNavigate();

  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  // Permission check
  useEffect(() => {
    if (!hasPermission("list-course", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to view Courses.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // Fetch courses with server-side pagination and search
  const fetchCourses = useCallback(async () => {
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);
    try {
      const response = await getcourses(
        recordsPerPage,
        currentPage,
        searchTerm,
        searchField,
      );
      const courses = response?.data || [];
      const pagination = response?.pagination || {};

      const pageOffset = (Number(currentPage) - 1) * recordsPerPage;
      const formattedData = courses.map((course, index) => ({
        ...course,
        srno: pageOffset + index + 1,
        createdAtFormatted: course.createdAt
          ? moment(course.createdAt).format("DD MMM YYYY, hh:mm A")
          : "N/A",
        updatedAtFormatted: course.updatedAt
          ? moment(course.updatedAt).format("DD MMM YYYY, hh:mm A")
          : "N/A",
      }));

      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setData((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...formattedData.filter((r) => !seen.has(r.id))];
        });
      } else {
        setData(formattedData);
      }
      appendRef.current = false;
      setTotalRecords(pagination.total || 0);
    } catch (err) {
      console.error("Error fetching courses:", err);
      toast.error("Failed to fetch course list");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [currentPage, recordsPerPage, searchTerm, searchField]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleDelete = async (id) => {
    const answer = await confirm({
      title: "Delete Confirmation",
      text: "Are you sure you want to delete this course?",
      tone: 'danger',
      confirmButtonText: "Yes, Delete it!",
    });

    if (answer.isConfirmed) {
      try {
        await deletecourses(id);
        // Confirmed in the popup, which then closes itself.
        toast.success("Course deleted successfully");
        appendRef.current = false;
        setCurrentPage(1);
        fetchCourses();
      } catch {
        toast.error("Failed to delete course");
      }
    }
  };

  const handleEdit = (id) => {
    navigate(`/edit-course/${id}`);
  };

  const hasMore = data.length < totalRecords;

  // The foot of the list asks for the next page when it comes into view.
  useEffect(() => {
    const node = listEndRef.current;
    if (!node || !hasMore || loading) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        // `loading` is state and lags a frame, so the ref is what stops
        // one pass asking for two pages.
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        appendRef.current = true;
        setLoadingMore(true);
        setCurrentPage((p) => p + 1);
      },
      { rootMargin: "150px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, data.length]);

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      appendRef.current = false;
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(newLimit);
    setCurrentPage(1);
  };

  const debouncedSearch = useMemo(
  () => debounce((value, field) => {
    appendRef.current = false;
    setSearchTerm(value);
    setSearchField(field);
    setCurrentPage(1);
  }, 500),
  []
);
const onSearchChange = (value, field) => {
  setUiSearchTerm(value);
  setUiSearchField(field);
  debouncedSearch(value, field);
};

  const headers = [
    {
      title: "Course",
      prop: "name",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-name">{row.name}</span>
          {row.code && <span className="lp-sub">{row.code}</span>}
        </div>
      ),
    },
    {
      title: "Duration",
      prop: "duration",
      sortable: true,
      cell: (row) => <span className="lp-plain">{row.duration || "—"}</span>,
    },
    {
      title: "Branch",
      prop: "branchOfCourse",
      cell: (row) => (
        <span className="lp-plain">{row.branchOfCourse || "—"}</span>
      ),
    },
    {
      title: "Department",
      prop: "departmentOfCourse",
      cell: (row) => (
        <span className="lp-plain">{row.departmentOfCourse || "—"}</span>
      ),
    },
    {
      title: "Updated",
      prop: "updatedAtFormatted",
      sortable: true,
      cell: (row) => <span className="lp-when">{row.updatedAtFormatted}</span>,
    },
  ];

  const searchableFields = [
    { value: "name", label: "Course Name" },
    { value: "code", label: "Code" },
    { value: "duration", label: "Duration" },
    { value: "branchOfCourse", label: "Branch" },
    { value: "departmentOfCourse", label: "Department" },
    { value: "description", label: "Description" },
  ];

  const canEdit = hasPermission("list-course", "update", "lms");
  const canDelete = hasPermission("list-course", "delete", "lms");

  return (
    <div className="pg lp">

      <header className="pg-head pg-head--bare">
        <div className="pg-head-actions">
          {hasPermission("list-course", "add", "lms") && (
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setFormModal({ recordId: null })}
            >
              Add course
            </button>
          )}
        </div>
      </header>

      {/* Everything that narrows the list, search included -- the table's
          own toolbar is switched off below so the two are not stacked one
          above the other. */}
      <FilterSheet label="Filter courses">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="lp-course-search">
              Search
            </label>
            <input
              id="lp-course-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by course name, code, branch or department..."
              value={uiSearchTerm}
              onChange={(e) => onSearchChange(e.target.value, uiSearchField)}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-course-field">
              Search in
            </label>
            <select
              id="lp-course-field"
              className="pg-select"
              value={uiSearchField}
              onChange={(e) => {
                setUiSearchField(e.target.value);
                onSearchChange(uiSearchTerm, e.target.value);
              }}
            >
              {searchableFields.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-course-rows">
              Rows per load
            </label>
            <select
              id="lp-course-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </FilterSheet>

      <div className="td-list td-list--lms">
        <NewCustomDataTable
          data={data}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => setDetails(row)}
          validRowClickTagNames="*"
        />
      </div>

      {/* Where the reader is, and the sentinel the observer watches. */}
      <div className="att-picklist-foot" ref={listEndRef}>
        {loadingMore ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more courses...</span>
          </>
        ) : hasMore ? (
          <span>
            {data.length} of {totalRecords} courses
          </span>
        ) : (
          totalRecords > 0 && (
            <span>
              All {totalRecords} course{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )
        )}
      </div>

      {/* Adding a course is one step -- six answers, asked once -- so the
          dialog holds the whole form rather than a strip of tabs. */}
      {formModal && (
        <div
          className="pg-modal pg-modal--over"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-course-form-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setFormModal(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide pg-modal-panel--form">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Course</span>
                <h3 className="pg-modal-title" id="lp-course-form-title">
                  {formModal.recordId ? "Edit course" : "Add a course"}
                </h3>
                <p className="pg-modal-sub">
                  Name it, code it, and say where it belongs.
                </p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setFormModal(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {formDone ? (
                <PopupDone message={formDone} onClose={() => setFormModal(null)} />
              ) : (
              <CourseModule
                embedded
                recordId={formModal.recordId}
                onCancel={() => setFormModal(null)}
                onDone={() => {
                  // Saved: the form's toast becomes this dialog's done panel,
                  // which closes the dialog by itself.
                  // The record changed, so the view popup still open behind this form
                  // would be showing the old one. It goes; the form stays.
                  setDetails(null);
                  appendRef.current = false;
                  setCurrentPage(1);
                  fetchCourses();
                }}
              />
              )}
            </div>
          </div>
        </div>
      )}

      {/* One course, in full. Description and the actions live here rather
          than in columns of their own -- a row is a thing you open, not a
          thing you aim at. */}
      {details && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-course-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Course</span>
                <h3 className="pg-modal-title" id="lp-course-title">
                  {details.name}
                </h3>
                {details.code && <p className="pg-modal-sub">{details.code}</p>}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {detailsDone ? (
                <PopupDone message={detailsDone} onClose={() => setDetails(null)} />
              ) : (
              <>
              <dl className="quiz-facts">
                <div>
                  <dt>Duration</dt>
                  <dd>{details.duration || "—"}</dd>
                </div>
                <div>
                  <dt>Branch</dt>
                  <dd>{details.branchOfCourse || "—"}</dd>
                </div>
                <div>
                  <dt>Department</dt>
                  <dd>{details.departmentOfCourse || "—"}</dd>
                </div>
                <div>
                  <dt>Added</dt>
                  <dd>{details.createdAtFormatted}</dd>
                </div>
                <div>
                  <dt>Updated</dt>
                  <dd>{details.updatedAtFormatted}</dd>
                </div>
              </dl>

              {details.description && (
                <>
                  <h5 className="pgm-section-title">Description</h5>
                  <p className="lp-prose">{details.description}</p>
                </>
              )}
              </>
              )}
            </div>

            {!detailsDone && (
            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                {canEdit && (
                  <button
                    type="button"
                    className="pgm-btn"
                    onClick={() => {
                      const id = details.id;
                      setFormModal({ recordId: id });
                    }}
                  >
                    Edit course
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="pgm-btn pgm-btn--danger"
                    onClick={() => {
                      const id = details.id;
                      handleDelete(id);
                    }}
                  >
                    Delete course
                  </button>
                )}
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setDetails(null)}
              >
                Close
              </button>
            </footer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListCourseModule;
